import { Controller } from "@hotwired/stimulus"
import ace from 'ace-builds/src-min-noconflict/ace'
import 'ace-builds/src-min-noconflict/mode-liquid'
import 'ace-builds/src-min-noconflict/mode-css'


export default class extends Controller {
  static values = {
    mode: String,
    readonly: Boolean
  }

  connect() {
    this.mount();

    // Pages that refresh by morphing (<meta name="turbo-refresh-method" content="morph">, e.g. the web
    // chat settings) diff the live DOM against fresh server HTML — which knows nothing about the editor
    // we injected. The morph deletes our <div> and strips the inline display:none back off the textarea,
    // leaving a bare one. Stimulus does NOT re-run connect(), because the textarea itself survived the
    // morph, so without this the editor is simply gone after every save.
    this.remount = this.remount.bind(this);
    document.addEventListener("turbo:morph", this.remount);
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.remount);
    this.teardown();
  }

  remount() {
    // Only rebuild if the morph actually took the editor out; otherwise we'd stack up a second one.
    if (this.isMounted()) return;

    this.teardown();
    this.mount();
  }

  mount() {
    if (this.isMounted()) return;

    this.editorElement = this.buildEditorElement();
    this.element.parentNode.insertAdjacentElement('beforebegin', this.editorElement);

    this.hideTextarea();
    this.instantiateEditor(this.editorElement);
  }

  teardown() {
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
    if (this.editorElement) {
      this.editorElement.remove();
      this.editorElement = null;
    }
    // Leave the textarea visible again: a half-torn-down field the merchant can still type into beats a
    // hidden one they can't.
    this.element.style.display = "";
    delete this.element.aceEditor;
  }

  isMounted() {
    return Boolean(this.editorElement && this.editorElement.isConnected);
  }

  buildEditorElement() {
    let editorDiv = document.createElement('div');
    editorDiv.style.height = '200px';
    editorDiv.style.display = 'block';
    // Look as unavailable as the disabled inputs beside it, not like an editor that ignores typing.
    if (this.readonlyValue) editorDiv.classList.add('opacity-60', 'cursor-not-allowed');

    return editorDiv;
  }

  hideTextarea() {
    this.element.style.display = "none";
  }

  instantiateEditor(editorDiv) {
    let stimulus = this;

    stimulus.editor = ace.edit(editorDiv);
    stimulus.editor.session.setMode("ace/mode/" + (stimulus.modeValue || 'json'));
    stimulus.editor.session.setValue(stimulus.element.value);
    stimulus.editor.setReadOnly(stimulus.readonlyValue);
    stimulus.editor.session.on('change', function(){
      stimulus.element.value = stimulus.editor.session.getValue();
      // The textarea's value is set directly (not typed into), so it never fires a native
      // 'input' event on its own — dispatch one so existing `data-action="input->..."` wiring
      // on the textarea (e.g. live preview refresh) keeps working under the ace editor.
      stimulus.element.dispatchEvent(new Event('input', { bubbles: true }));
    });

    // Other controllers (e.g. inserting a recipe snippet) write to the textarea's `.value` directly,
    // which the ace editor wouldn't otherwise notice. Exposing the editor lets them push it in too.
    stimulus.element.aceEditor = stimulus.editor;
  }
}
